import React, { useMemo, useRef, useState } from 'react';
import { Button, Alert } from 'react-bootstrap';
import axios from 'axios';
import PdfPreviewModal from '../../components/common/PdfPreviewModal';

// Lets the examiner type and format the "Report" required document directly
// in the browser (bold/italic/underline/lists - deliberately limited to
// what the backend can faithfully lay out in the generated PDF) instead of
// preparing a file elsewhere. On save, the typed HTML is sent to the
// backend, rendered into a PDF matching UVTAB's assessment report template,
// and saved as the "Report" document - same effect as uploading a file.
// Uses the browser's native contentEditable/execCommand rather than a
// third-party rich-text library, keeping the formatting surface small and
// dependency-free.
export default function ReportEditor({ assignmentId, examCentreId, alreadySaved, uploadWindowOpen, onSaved }) {
  const editorRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [empty, setEmpty] = useState(true);
  const [showPreview, setShowPreview] = useState(false);
  const [showDraftPreview, setShowDraftPreview] = useState(false);
  const [draftHtml, setDraftHtml] = useState('');

  const disabled = alreadySaved || !uploadWindowOpen;

  // Memoized so PdfPreviewModal's fetch effect (keyed on this object) only
  // re-runs when the draft text actually changes, not on every re-render of
  // this component - ExamUploadPage ticks a "now" clock every second for
  // its countdown display, which would otherwise recreate this object (and
  // silently re-fetch/reload the preview) once a second.
  const draftReportBody = useMemo(() => ({ contentHtml: draftHtml }), [draftHtml]);

  const exec = (command) => {
    if (disabled) return;
    editorRef.current?.focus();
    document.execCommand(command, false, null);
  };

  const handleInput = () => {
    const text = editorRef.current?.innerText || '';
    setEmpty(text.trim().length === 0);
  };

  const handlePreviewDraft = () => {
    const html = editorRef.current?.innerHTML || '';
    if (!html.trim() || empty) return;
    setDraftHtml(html);
    setShowDraftPreview(true);
  };

  const handleSave = async () => {
    const html = editorRef.current?.innerHTML || '';
    const text = editorRef.current?.innerText || '';
    if (!text.trim()) {
      setError('Please type the report text before saving.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await axios.post(`/api/assignments/${assignmentId}/exam-centers/${examCentreId}/report`, { contentHtml: html });
      onSaved();
    } catch (err) {
      const backendMessage = typeof err.response?.data === 'string' ? err.response.data : null;
      setError(backendMessage || 'Error saving report.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      {error && <Alert variant="danger" className="py-2">{error}</Alert>}
      {alreadySaved && (
        <Alert variant="success" className="py-2">
          Report saved and attached.{' '}
          <Button variant="link" size="sm" className="p-0 align-baseline" onClick={() => setShowPreview(true)}>
            Preview
          </Button>
        </Alert>
      )}
      <div className="d-flex flex-wrap gap-1 mb-2">
        <Button size="sm" variant="outline-secondary" disabled={disabled} onClick={() => exec('bold')}>
          <strong>B</strong>
        </Button>
        <Button size="sm" variant="outline-secondary" disabled={disabled} onClick={() => exec('italic')}>
          <em>I</em>
        </Button>
        <Button size="sm" variant="outline-secondary" disabled={disabled} onClick={() => exec('underline')}>
          <u>U</u>
        </Button>
        <Button size="sm" variant="outline-secondary" disabled={disabled} onClick={() => exec('insertUnorderedList')}>
          Bullet List
        </Button>
        <Button size="sm" variant="outline-secondary" disabled={disabled} onClick={() => exec('insertOrderedList')}>
          Numbered List
        </Button>
      </div>
      <div
        ref={editorRef}
        contentEditable={!disabled}
        suppressContentEditableWarning
        onInput={handleInput}
        className="form-control"
        style={{ minHeight: '200px', backgroundColor: disabled ? '#e9ecef' : '#fff', overflowY: 'auto' }}
      />
      <div className="mt-2">
        {!alreadySaved && (
          <Button size="sm" variant="outline-secondary" className="me-2" disabled={disabled || empty} onClick={handlePreviewDraft}>
            Preview
          </Button>
        )}
        <Button size="sm" variant="primary" disabled={disabled || saving || empty} onClick={handleSave}>
          {saving ? 'Generating PDF...' : alreadySaved ? 'Report Saved' : 'Save Report'}
        </Button>
        {!alreadySaved && (
          <span className="text-muted small ms-2">
            Preview shows the exact PDF - including circumstances (absent/empty/protest candidates) appended
            automatically - before you save. Saving formats this text into the final Report document.
          </span>
        )}
      </div>
      <PdfPreviewModal
        show={showPreview}
        onClose={() => setShowPreview(false)}
        title="Assessment Report"
        fetchUrl={`/api/assignments/${assignmentId}/report/preview`}
      />
      <PdfPreviewModal
        show={showDraftPreview}
        onClose={() => setShowDraftPreview(false)}
        title="Assessment Report (Draft Preview)"
        fetchUrl={`/api/assignments/${assignmentId}/exam-centers/${examCentreId}/report/preview-draft`}
        method="POST"
        body={draftReportBody}
      />
    </div>
  );
}
